/**
 * avatarPreloader — loads a GLB once, keeps it in memory.
 * Concurrent callers share the same in-flight Promise (no double-fetch).
 */

import type * as THREE from "three";

export interface CachedGLTF {
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
}

const _cache = new Map<string, CachedGLTF>();
const _promises = new Map<string, Promise<CachedGLTF>>();

export async function preloadGLTF(url: string): Promise<CachedGLTF> {
  const hit = _cache.get(url);
  if (hit) return hit;

  const inflight = _promises.get(url);
  if (inflight) return inflight;

  const promise = (async (): Promise<CachedGLTF> => {
    const [{ GLTFLoader }, { DRACOLoader }] = await Promise.all([
      import("three/examples/jsm/loaders/GLTFLoader.js"),
      import("three/examples/jsm/loaders/DRACOLoader.js"),
    ]);

    const draco = new DRACOLoader();
    // Self-hosted decoder (vendored from three@0.183.2 — see
    // public/draco/0.183.2/README.md). The previous gstatic.com CDN fetch
    // failed on some devices/networks (Sentry VYV-FRONTEND-2J) and is a
    // third-party runtime dependency we don't need.
    draco.setDecoderPath("/draco/0.183.2/");

    const loader = new GLTFLoader();
    loader.setDRACOLoader(draco);

    let gltf: { scene: THREE.Group; animations: THREE.AnimationClip[] };
    try {
      gltf = await new Promise<{ scene: THREE.Group; animations: THREE.AnimationClip[] }>(
        (resolve, reject) => loader.load(url, (loaded) => resolve(loaded), undefined, reject)
      );
    } finally {
      // Failed requests must release workers too. Decoder fetch is lazy: models
      // without Draco should not download/start a decoder at all.
      draco.dispose();
    }

    const result: CachedGLTF = { scene: gltf.scene, animations: gltf.animations ?? [] };
    _cache.set(url, result);
    _promises.delete(url);
    return result;
  })().catch((err) => {
    // Rejeição NUNCA fica cacheada (fix 09/08): sem este delete, a primeira
    // falha (rede oscilou, 403 transitório) deixava a promise REJEITADA em
    // _promises e toda chamada futura da mesma URL a recebia de volta — a URL
    // morria até o reload da página. Vale para modelos e para os clipes /mp/.
    _promises.delete(url);
    throw err;
  });

  _promises.set(url, promise);
  return promise;
}

export function getGLTFFromCache(url: string): CachedGLTF | undefined {
  return _cache.get(url);
}

// ── Legacy aliases (keeps backward-compat with remote API) ──────────────────
const DEFAULT_URL = "/avatar/model_small.glb";
export const preloadAvatar = (url = DEFAULT_URL) => preloadGLTF(url);
export const getCachedAvatar = () => getGLTFFromCache(DEFAULT_URL) ?? null;
